import { insideSingleLevelSameOriginIframe, topLevelNonIframe } from ':fat/helpers/iframe';
import { withRetry } from ':fat/helpers/retry';
import { BlockchainWorker } from ':fat/messaging/entities/blockchainWorker';
import { DatabaseWorker } from ':fat/messaging/entities/databaseWorker';
import { MainPage } from ':fat/messaging/entities/mainPage';
import { sanityCheck as mainPageSanityCheck } from ':fat/messaging/entities/mainPage/sanityCheck';
import { SecurityFrame } from ':fat/messaging/entities/securityFrame';

import { isRelevantFATMessage } from './filtering';
import { logMessage } from './logging';
import { Poster, POSTERS, Targets } from './posters';

// This is a singleton that each entity will initialize early on.
// It's made available as a global to avoid circular import issues.
declare global {
  let MessageBus: MessageBusImpl;

  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    MessageBus: MessageBusImpl;
  }
}

export function initializeMessageBus<E extends Entity>(
  entity: E,
  handlerContext: HandlerContexts[E],
) {
  self.MessageBus = new MessageBusImpl(entity, handlerContext);
}

// Used to control logging during development
const LOG_SEND = false;
const LOG_RECEIVE = true;

type WaitingInfo<M extends AnyTaggedMessage = AnyTaggedMessage> = PromiseWithResolvers<M> & {
  originalMessage: TaggedMessage<Entity>;
};

class MessageBusImpl {
  // Always set to the entity this MessageBus is running in
  #entity: Entity;

  // A this-bound version of `this.receive`
  #boundHandler: (event: MessageEvent) => void;

  // The context object to pass to the handler
  #handlerContext: AnyHandlerContext;

  // Messages that are waiting for an asynchronous reply
  #waiting: Map<MessageID, WaitingInfo<AnyTaggedMessage>>;

  // Used to send messages to other entities. These are set when various parts
  // of the app call the `MessageBus.register*` methods.
  #references: {
    // Will only be set on the main page.
    securityFrame?: HTMLIFrameElement;
    blockchainWorker?: Worker;
    databaseWorker?: Worker;

    // Will only be set within the database worker
    blockchainWorkerPort?: MessagePort;

    // Will only be set within the blockchain worker
    databaseWorkerPort?: MessagePort;
  };

  constructor(entity: Entity, handlerContext: AnyHandlerContext) {
    this.#entity = entity;
    this.ensureProperContext();

    this.#boundHandler = this.startListening();
    this.#handlerContext = handlerContext;
    this.#waiting = new Map();
    this.#references = {};
  }

  async send<
    From extends Entity = never,
    To extends Entity = never,
    ReplyKind extends AnyValidMessage['kind'] | undefined = never,
    Return = ReplyKind extends AnyValidMessage['kind'] ? MessageWithKind<ReplyKind> : never,
  >(
    to: To,
    message: ValidMessages[To],
    options: MessageOptions & { expectReply?: ReplyKind } = {},
  ): Promise<Return> {
    const from = this.#entity as From;
    const send = this.getSendingFunction(from, to, message);

    const target = await withRetry(() => this.getTarget(from, to), { retries: 5, interval: 50 });

    const taggedMessage = send(message, target, options);
    if (LOG_SEND) {
      logMessage(taggedMessage);
    }

    if (options.expectReply) {
      const waitingInfo = {
        ...Promise.withResolvers<AnyTaggedMessage>(),
        originalMessage: taggedMessage,
      };
      this.#waiting.set(taggedMessage.id, waitingInfo);

      // This promise will be resolved in `this.receive` when a reply comes
      return waitingInfo.promise as Promise<Return>;
    }

    // No reply is expected, so resolve the promise immediately
    return undefined as never;
  }

  receive(event: MessageEvent) {
    if (isRelevantFATMessage(event, this.#entity)) {
      const message = event.data;
      const waitingInfo: WaitingInfo | undefined = message.replyingTo
        ? this.#waiting.get(message.replyingTo)
        : undefined;

      if (message.replyingTo && waitingInfo) {
        // The message is a reply, and the sender of the original message is
        // waiting for it. Resolve that promise with the message we received
        // instead of calling the handler.
        if (LOG_RECEIVE) logMessage(message, waitingInfo.originalMessage);
        if (message.rejection) {
          waitingInfo.reject(message);
        } else {
          waitingInfo.resolve(message);
        }
        this.#waiting.delete(message.replyingTo);
      } else {
        // The original sender is not waiting for a reply, so this message
        // should be handled asynchronously using the normal handler
        if (LOG_RECEIVE) logMessage(message);
        this.callHandler(message);
      }
    }
  }

  async reply(
    toMessage: AnyTaggedMessage,
    withMessage: AnyValidMessage,
    options: MessageOptions = {},
  ) {
    return this.send(toMessage.from, withMessage, { ...options, replyingTo: toMessage.id });
  }

  private callHandler(message: AnyTaggedMessage) {
    switch (this.#entity) {
      case 'mainPage':
        void MainPage.handleMessage(
          this.#handlerContext as MainPage.HandlerContext,
          message as TaggedMessage<'mainPage'>,
        );
        break;
      case 'securityFrame':
        void SecurityFrame.handleMessage(
          this.#handlerContext as SecurityFrame.HandlerContext,
          message as TaggedMessage<'securityFrame'>,
        );
        break;
      case 'blockchainWorker':
        void BlockchainWorker.handleMessage(
          this.#handlerContext as BlockchainWorker.HandlerContext,
          message as TaggedMessage<'blockchainWorker'>,
        );
        break;
      case 'databaseWorker':
        void DatabaseWorker.handleMessage(
          this.#handlerContext as DatabaseWorker.HandlerContext,
          message as TaggedMessage<'databaseWorker'>,
        );
        break;
      default:
    }
  }

  private startListening() {
    const handler = this.receive.bind(this);
    self.addEventListener('message', handler);
    return handler;
  }

  stopListening() {
    const handler = this.#boundHandler;
    if (!handler) return;

    switch (this.#entity) {
      case 'mainPage':
        window.removeEventListener('message', handler);
        this.withEachWorker((worker) => {
          worker?.removeEventListener('message', handler);
        });
        break;
      case 'securityFrame':
        self.removeEventListener('message', handler);
        break;
      case 'blockchainWorker':
        self.removeEventListener('message', handler);
        this.#references.databaseWorkerPort?.removeEventListener('message', handler);
        break;
      case 'databaseWorker':
        self.removeEventListener('message', handler);
        this.#references.blockchainWorkerPort?.removeEventListener('message', handler);
        break;
      default:
    }
  }

  registerWorkerPort(which: 'blockchainWorkerPort' | 'databaseWorkerPort', port: MessagePort) {
    const handler = this.#boundHandler;
    if (!handler) {
      throw new Error(`Handler not set yet, can't register ${which}`);
    }

    this.#references[which] = port;
    port.addEventListener('message', handler);
    port.start();
  }

  registerWorkers(blockchainWorker: Worker, databaseWorker: Worker) {
    const handler = this.#boundHandler;
    if (!handler) {
      throw new Error(`Handler not set yet, can't register workers`);
    }

    this.#references.blockchainWorker = blockchainWorker;
    this.#references.databaseWorker = databaseWorker;
    this.withEachWorker((worker) => {
      worker.addEventListener('message', handler);
    });
  }

  registerSecurityFrame(securityFrame: HTMLIFrameElement) {
    this.#references.securityFrame = securityFrame;
    securityFrame.addEventListener('load', () => {
      // The security frame will always be registered last, since the hook that
      // registers it is rendered inside the EnsureMessagingAndWorkersAreSetup.
      // This means that everything is hooked up the way it should be, so we'll
      // now perform a "sanity check" to make sure things are working right.
      void this.sanityCheck();
    });
  }

  setHandlerContext<E extends Entity>(entity: E, handlerContext: HandlerContexts[E]) {
    if (entity !== this.#entity) {
      throw new Error(`Trying to set a ${entity} handler context for a ${this.#entity} MessageBus`);
    }

    this.#handlerContext = handlerContext;
  }

  private async sanityCheck() {
    if (this.#entity !== 'mainPage') {
      throw new Error('Sanity check can only be performed by the main page');
    }

    const results = await Promise.all([
      mainPageSanityCheck(),
      SecurityFrame.actions.sanityCheck(),
      BlockchainWorker.actions.sanityCheck(),
      DatabaseWorker.actions.sanityCheck(),
    ]);

    if (results.every((result) => result.success)) {
      self.SANITY_CHECK_COMPLETE = true;
    } else {
      // TODO: if the sanity check failed for any reason, do something that
      // renders an error screen of some sort
      // eslint-disable-next-line no-console
      console.error('At least one sanity check failed');
    }
  }

  private withEachWorker(func: (worker: Worker) => void) {
    if (!this.#references.blockchainWorker || !this.#references.databaseWorker) {
      throw new Error('Workers not initialized yet');
    }

    [this.#references.blockchainWorker, this.#references.databaseWorker].forEach(func);
  }

  private ensureProperContext() {
    if (this.#entity === 'mainPage' && !topLevelNonIframe()) {
      throw new Error('Must be in main page');
    }

    if (this.#entity === 'securityFrame' && !insideSingleLevelSameOriginIframe()) {
      throw new Error('Must be in security frame');
    }
  }

  private getSendingFunction<From extends Entity, To extends Entity>(
    from: From,
    to: To,
    message: ValidMessages[To],
  ): Poster<From, To> {
    const send = POSTERS[from]?.[to] as Poster<From, To>;

    if (!send) {
      throw new Error(`Cannot send a message (${message.kind}) to ${to} from ${this.#entity}`);
    }

    return send;
  }

  private getTarget<From extends Entity, To extends Entity>(from: From, to: To): Targets[From][To] {
    const targetFinders: Record<Entity, Partial<Record<Entity, () => unknown>>> = {
      mainPage: {
        mainPage: () => window,
        securityFrame: () => this.#references.securityFrame?.contentWindow,
        blockchainWorker: () => this.#references.blockchainWorker,
        databaseWorker: () => this.#references.databaseWorker,
      },
      securityFrame: {
        mainPage: () => window.parent,
      },
      blockchainWorker: {
        mainPage: () => self,
        databaseWorker: () => this.#references.databaseWorkerPort,
      },
      databaseWorker: {
        mainPage: () => self,
        blockchainWorker: () => this.#references.blockchainWorkerPort,
      },
    } as const;

    const target = targetFinders[from]?.[to]?.();

    if (!target) {
      throw new Error(`Target for ${to} (from ${this.#entity}) not found`);
    }

    return target as Targets[From][To];
  }
}
